import type { User } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DISCOVERY_PAGE_SIZE } from '@/constants/discoveryFilters';
import { clearScrollPositions } from '@/hooks/useScrollRestoration';
import { analyticsClient } from '@/lib';
import { authService } from '@/services';
import { useUIStore } from '@/stores';

let lastSeenUserId: string | null = null;

// Discovery scroll offset and loaded range are session state; a different
// account (or a fresh login) must not inherit them.
function resetDiscoveryState() {
  clearScrollPositions();
  useUIStore.getState().setDiscoveryDisplayCount(DISCOVERY_PAGE_SIZE);
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(u => {
      setUser(u);
      setLoading(false);

      if (u) {
        // Fresh login or account switch (same-uid token refreshes keep state).
        if (lastSeenUserId !== u.uid) {
          if (lastSeenUserId) analyticsClient.reset();
          resetDiscoveryState();
        }
        lastSeenUserId = u.uid;
      } else if (lastSeenUserId) {
        analyticsClient.reset();
        lastSeenUserId = null;
        resetDiscoveryState();
      }

      // Clear all queries when user signs out
      if (!u) {
        queryClient.clear();
      }
    });

    return () => unsubscribe();
  }, [queryClient]);

  const signOut = async () => {
    await authService.signOut();
  };

  return {
    user,
    loading,
    signOut,
  };
}
